/** 年级 */
export enum Grade {
  /** 一年级 */
  PRIMARY_ONE = 'primary_one',
  /** 二年级 */
  PRIMARY_TWO = 'primary_two',
  /** 三年级 */
  PRIMARY_THREE = 'primary_three',
  /** 四年级 */
  PRIMARY_FOUR = 'primary_four',
  /** 五年级 */
  PRIMARY_FIVE = 'primary_five',
  /** 六年级 */
  PRIMARY_SIX = 'primary_six',
  /** 七年级 */
  SEVEN = 'seven',
  /** 八年级 */
  EIGHT = 'eight',
  /** 九年级 */
  NINE = 'nine',
  /** 中考 */
  ZHONGKAO = 'zhongkao',
  /** 必修一 */
  HIGH_ONE = 'high_one',
  /** 必修二 */
  HIGH_TWO = 'high_two',
  /** 必修三 */
  HIGH_THREE = 'high_three',
  /** 选修一 */
  SELECTIVE_ONE = 'selective_one',
  /** 选修二 */
  SELECTIVE_TWO = 'selective_two',
  /** 选修三 */
  SELECTIVE_THREE = 'selective_three',
  /** 高考 */
  GAOKAO = 'gaokao',
  /** 通用 */
  COMMON = 'common'
}

/** 题目内容类型 */
export enum QuestionContentType {
  /** span文本 */
  BLOCK_TEXT = 'block_text',
  /** 文本 */
  TEXT = 'text',
  /** 公式 */
  KATEX = 'katex',
  /** 图片 */
  IMAGE = 'image',
  /** 块级 */
  BLOCK = 'block'
}

/** 学科 */
export enum Subject {
  /** 数学 */
  MATH = 'math',
  /** 物理 */
  PHYSICS = 'physics',
  /** 化学 */
  CHEMISTRY = 'chemistry'
}

/** 教育阶段 */
export enum EducationStage {
  /** 小学 */
  PRIMARY_SCHOOL = 'primary_school',
  /** 初中 */
  JUNIOR_HIGH = 'junior_high',
  /** 高中 */
  SENIOR_HIGH = 'senior_high'
}

/** 学期 */
export enum Semester {
  /** 上学期 */
  FIRST = 'first',
  /** 下学期 */
  SECOND = 'second'
}

/** 学习力等级 */
export enum LearningLevel {
  /** A */
  A = 'A',
  /** B */
  B = 'B',
  /** C */
  C = 'C',
  /** D */
  D = 'D'
}

/** 题目类型 */
export enum QuestionType {
  /** 填空 */
  FILL_BLANK = 'fill_blank',
  /** 解答题 */
  SHORT_ANSWER = 'short_answer',
  /** 应用题 */
  APPLICATION = 'application',
  /** 选择题 */
  SELECT = 'select'
}

/** 模块（核心知识点学习，知识点巩固记忆学习，典例精学，牛刀小试，智适化匹配练习） */
export enum QuestionModule {
  /** 核心知识点学习 */
  CORE = 'core',
  /** 知识点巩固记忆学习 */
  STRENGTHEN = 'strengthen',
  /** 典例精学 */
  EXAMPLE = 'example',
  /** 牛刀小试 */
  TEST = 'test',
  /** 智适化匹配练习 */
  MATCH = 'match'
}

/** 年级映射 */
export const gradeMap: Record<Grade, string> = {
  [Grade.PRIMARY_ONE]: '一年级',
  [Grade.PRIMARY_TWO]: '二年级',
  [Grade.PRIMARY_THREE]: '三年级',
  [Grade.PRIMARY_FOUR]: '四年级',
  [Grade.PRIMARY_FIVE]: '五年级',
  [Grade.PRIMARY_SIX]: '六年级',
  [Grade.SEVEN]: '七年级',
  [Grade.EIGHT]: '八年级',
  [Grade.NINE]: '九年级',
  [Grade.ZHONGKAO]: '中考',
  [Grade.HIGH_ONE]: '必修一',
  [Grade.HIGH_TWO]: '必修二',
  [Grade.HIGH_THREE]: '必修三',
  [Grade.SELECTIVE_ONE]: '选修一',
  [Grade.SELECTIVE_TWO]: '选修二',
  [Grade.SELECTIVE_THREE]: '选修三',
  [Grade.GAOKAO]: '高考',
  [Grade.COMMON]: '通用'
}

/** 题目类型映射 */
export const questionTypeMap: Record<QuestionType, string> = {
  [QuestionType.FILL_BLANK]: '填空题',
  [QuestionType.SHORT_ANSWER]: '解答题',
  [QuestionType.APPLICATION]: '应用题',
  [QuestionType.SELECT]: '选择题'
}

/** 题目内容类型映射 */
export const questionContentTypeMap: Record<QuestionContentType, string> = {
  [QuestionContentType.TEXT]: '文本',
  [QuestionContentType.KATEX]: '公式',
  [QuestionContentType.IMAGE]: '图片',
  [QuestionContentType.BLOCK]: '块级',
  [QuestionContentType.BLOCK_TEXT]: '块级文本'
}

/** 学科映射 */
export const subjectMap: Record<Subject, string> = {
  [Subject.MATH]: '数学',
  [Subject.PHYSICS]: '物理',
  [Subject.CHEMISTRY]: '化学'
}

/** 教育阶段映射 */
export const educationStageMap: Record<EducationStage, string> = {
  [EducationStage.PRIMARY_SCHOOL]: '小学',
  [EducationStage.JUNIOR_HIGH]: '初中',
  [EducationStage.SENIOR_HIGH]: '高中'
}

/** 各教育阶段对应年级（含通用） */
export const gradesByEducationStage: Record<EducationStage, Grade[]> = {
  [EducationStage.PRIMARY_SCHOOL]: [
    Grade.PRIMARY_ONE,
    Grade.PRIMARY_TWO,
    Grade.PRIMARY_THREE,
    Grade.PRIMARY_FOUR,
    Grade.PRIMARY_FIVE,
    Grade.PRIMARY_SIX,
    Grade.COMMON,
  ],
  [EducationStage.JUNIOR_HIGH]: [
    Grade.SEVEN,
    Grade.EIGHT,
    Grade.NINE,
    Grade.ZHONGKAO,
    Grade.COMMON,
  ],
  [EducationStage.SENIOR_HIGH]: [
    Grade.HIGH_ONE,
    Grade.HIGH_TWO,
    Grade.HIGH_THREE,
    Grade.SELECTIVE_ONE,
    Grade.SELECTIVE_TWO,
    Grade.SELECTIVE_THREE,
    Grade.GAOKAO,
    Grade.COMMON,
  ],
}

/**
 * 按教育阶段取年级列表；未传阶段时返回全部年级。
 * @param stage 教育阶段枚举值
 */
export function getGradesByStage(stage?: string): Grade[] {
  if (!stage) return Object.values(Grade)
  const list = gradesByEducationStage[stage as EducationStage]
  return list ?? Object.values(Grade)
}

/**
 * 按教育阶段取年级下拉选项（value / label）。
 * @param stage 教育阶段枚举值
 */
export function getGradeOptions(stage?: string): { value: string; label: string }[] {
  return getGradesByStage(stage).map((grade) => ({
    value: grade,
    label: gradeMap[grade],
  }))
}

/** 学期映射 */
export const semesterMap: Record<Semester, string> = {
  [Semester.FIRST]: '上',
  [Semester.SECOND]: '下'
}

/** 学习力等级映射 */
export const learningLevelMap: Record<LearningLevel, string> = {
  [LearningLevel.A]: 'A',
  [LearningLevel.B]: 'B',
  [LearningLevel.C]: 'C',
  [LearningLevel.D]: 'D'
}

/** 模块映射 */
export const questionModuleMap: Record<QuestionModule, string> = {
  [QuestionModule.CORE]: '核心知识点学习',
  [QuestionModule.STRENGTHEN]: '知识点巩固记忆学习',
  [QuestionModule.EXAMPLE]: '典例精学',
  [QuestionModule.TEST]: '牛刀小试',
  [QuestionModule.MATCH]: '巩固练习'
}

/** 试卷类型枚举 */
export enum QuestionPaperType {
  /** 教案卷(知识点试卷) */
  TEACHING_PLAN = 'teaching_plan',
  /** 阶段卷 */
  STAGE_EXAM = 'stage_exam',
  /** 章节卷 */
  CHAPTER_EXAM = 'chapter_exam'
}

export const questionPaperMap = {
  [QuestionPaperType.TEACHING_PLAN]: '教案卷',
  [QuestionPaperType.STAGE_EXAM]: '阶段卷',
  [QuestionPaperType.CHAPTER_EXAM]: '章节卷'
}
